/**
 * Lead follow-ups (the phone's Follow-ups feed) + Done/Undo — 2026-10-07,
 * Manak option 1. Same backend calls as the AURA Sales app:
 *   GET  /lead-integrations/follow-ups
 *   POST /lead-integrations/lead-imports/:id/follow-up-done
 *   POST /lead-integrations/lead-imports/:id/follow-up-undo { followUpDate }
 */
import { api } from '@/utils/api';
import { joinPersonName } from '@/utils/names';

export interface LeadFollowUpRow {
  id: string; // LeadImport id
  contactName?: string | null;
  phone?: string | null;
  scheduledAt: string | null;
  status: string;
  canonicalStage?: string | null;
  isWon?: boolean;
  notes?: string | null;
  contact?: { firstName?: string | null; lastName?: string | null; phone?: string | null; mobile?: string | null } | null;
}

/** How long the Undo toast stays up. */
export const UNDO_MS = 5000;

export async function fetchLeadFollowUps(): Promise<LeadFollowUpRow[]> {
  const res = await api.get('/lead-integrations/follow-ups');
  if (res.data?.success === false) throw new Error(res.data?.error || 'Could not load follow-ups');
  return (Array.isArray(res.data?.data) ? res.data.data : []) as LeadFollowUpRow[];
}

/** Returns the cleared date (ISO) for Undo. */
export async function markFollowUpDone(leadId: string): Promise<string | null> {
  const res = await api.post(`/lead-integrations/lead-imports/${leadId}/follow-up-done`);
  return res.data?.data?.previousFollowUpDate ?? null;
}

export async function undoFollowUpDone(leadId: string, followUpDate: string): Promise<void> {
  await api.post(`/lead-integrations/lead-imports/${leadId}/follow-up-undo`, { followUpDate });
}

export function isWonRow(r: LeadFollowUpRow): boolean {
  return r.isWon === true || (r.canonicalStage ?? '').toLowerCase() === 'won';
}

export function rowName(r: LeadFollowUpRow): string {
  const joined = joinPersonName(r.contact?.firstName, r.contact?.lastName).trim();
  return joined || (r.contactName ?? '').trim() || (r.phone ?? '').trim() || 'Contact name not recorded';
}

const SHORT_DATE = new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });

export function wonFollowUpPromptText(name: string, followUpDateIso: string): string {
  return `${name} has a follow-up due on ${SHORT_DATE.format(new Date(followUpDateIso))}. Mark that follow-up as done?`;
}
